import { FC } from 'react';
import {
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { FIT_BUCKET_OPTIONS } from './fit-bucket.util';

export const ALL_STATUSES = [
  'New',
  'Send outreach',
  'Needs human outreach',
  'Assigned to recruiter',
  'Declined by admin',
] as const;

export type ConsoleStatus = typeof ALL_STATUSES[number];

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilters: string[];
  onStatusFiltersChange: (statuses: string[]) => void;
  statusMenuOpen: boolean;
  onStatusMenuToggle: () => void;
  /** Status options shown in the dropdown (varies by tab). Display-label strings. */
  statusOptions: readonly string[];
  /** When true the status filter is disabled (e.g. needs-human has a single status). */
  statusDisabled?: boolean;
  fitBuckets: string[];
  onFitBucketsChange: (b: string[]) => void;
  fitMenuOpen: boolean;
  onFitMenuToggle: () => void;
  sortByFit: boolean;
  onSortByFitToggle: () => void;
  totalApplicants: number;
  totalCompanies: number;
  isFiltering: boolean;
  onClearFilters: () => void;
}

const statusMenuLabel = (filters: string[]): string => {
  if (filters.length === 0) return 'Status';
  if (filters.length === 1) return filters[0];
  return `${filters.length} statuses`;
};

const fitMenuLabel = (buckets: string[]): string => {
  if (buckets.length === 0) return 'Fit Score';
  if (buckets.length === 1) {
    const found = FIT_BUCKET_OPTIONS.find((o) => o.value === buckets[0]);
    return found ? found.label : buckets[0];
  }
  return `${buckets.length} fit scores`;
};

const ConsoleToolbar: FC<Props> = ({
  search,
  onSearchChange,
  statusFilters,
  onStatusFiltersChange,
  statusMenuOpen,
  onStatusMenuToggle,
  statusOptions,
  statusDisabled,
  fitBuckets,
  onFitBucketsChange,
  fitMenuOpen,
  onFitMenuToggle,
  sortByFit,
  onSortByFitToggle,
  totalApplicants,
  totalCompanies,
  isFiltering,
  onClearFilters,
}) => {
  const toggleStatus = (status: string) => {
    if (statusFilters.includes(status)) {
      onStatusFiltersChange(statusFilters.filter((s) => s !== status));
    } else {
      onStatusFiltersChange([...statusFilters, status]);
    }
  };

  const clearStatuses = () => {
    onStatusFiltersChange([]);
  };

  const toggleFit = (bucket: string) => {
    if (fitBuckets.includes(bucket)) {
      onFitBucketsChange(fitBuckets.filter((b) => b !== bucket));
    } else {
      onFitBucketsChange([...fitBuckets, bucket]);
    }
  };

  const clearFits = () => {
    onFitBucketsChange([]);
  };

  return (
    <div className="ai-console__toolbar">
      <div className="ai-console__toolbar-controls">
        <Input
          type="search"
          className="ai-console__search"
          placeholder="Search name, email, company, or job title…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <Dropdown
          isOpen={statusMenuOpen}
          toggle={onStatusMenuToggle}
          className="ai-console__status-dropdown"
        >
          <DropdownToggle
            caret
            className="ai-console__status-toggle"
            disabled={statusDisabled}
          >
            {statusDisabled
              ? statusOptions[0] ?? 'All statuses'
              : statusMenuLabel(statusFilters)}
          </DropdownToggle>
          <DropdownMenu>
            {statusOptions.map((status) => (
              <DropdownItem
                key={status}
                toggle={false}
                onClick={() => toggleStatus(status)}
                className="ai-console__status-item"
              >
                <Input
                  type="checkbox"
                  checked={statusFilters.includes(status)}
                  onChange={() => toggleStatus(status)}
                  className="ai-console__status-checkbox"
                />
                {status}
              </DropdownItem>
            ))}
            {statusFilters.length > 0 && (
              <>
                <DropdownItem divider />
                <DropdownItem onClick={clearStatuses} className="ai-console__status-clear">
                  Clear statuses
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </Dropdown>

        <Dropdown
          isOpen={fitMenuOpen}
          toggle={onFitMenuToggle}
          className="ai-console__fit-dropdown"
        >
          <DropdownToggle
            caret
            className="ai-console__fit-toggle"
          >
            {fitMenuLabel(fitBuckets)}
          </DropdownToggle>
          <DropdownMenu>
            {FIT_BUCKET_OPTIONS.map((o) => (
              <DropdownItem
                key={o.value}
                toggle={false}
                onClick={() => toggleFit(o.value)}
                className="ai-console__fit-item"
              >
                <Input
                  type="checkbox"
                  checked={fitBuckets.includes(o.value)}
                  onChange={() => toggleFit(o.value)}
                  className="ai-console__fit-checkbox"
                />
                {o.label}
              </DropdownItem>
            ))}
            {fitBuckets.length > 0 && (
              <>
                <DropdownItem divider />
                <DropdownItem onClick={clearFits} className="ai-console__fit-clear">
                  Clear fit scores
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </Dropdown>

        <button
          type="button"
          className={`ai-console__sort-toggle${sortByFit ? ' ai-console__sort-toggle--active' : ''}`}
          onClick={onSortByFitToggle}
          aria-pressed={sortByFit}
          title="Sort applicants by AI fit score, highest first"
        >
          <span className="ai-console__sort-icon" aria-hidden="true">⇅</span>
          Sort by fit
        </button>
      </div>

      {isFiltering && (
        <div className="ai-console__result-summary">
          <span className="ai-console__result-text">
            {totalApplicants} {totalApplicants === 1 ? 'result' : 'results'} across{' '}
            {totalCompanies} {totalCompanies === 1 ? 'company' : 'companies'}
          </span>
          <button
            type="button"
            className="ai-console__clear-link"
            onClick={onClearFilters}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsoleToolbar;
